import { useCallback, useMemo } from 'react'
import { startAuthentication } from '@simplewebauthn/browser'
import { authService } from '@/services/auth'

interface AuthResult {
  success: boolean
  error?: string
  user?: {
    id: string
    displayName: string
    email: string
  }
}

/**
 * Custom hook for WebAuthn authentication.
 *
 * Handles the full FIDO2 assertion ceremony:
 * 1. Requests challenge from backend (/api/auth/begin)
 * 2. Calls navigator.credentials.get() via SimpleWebAuthn
 * 3. Sends assertion response to backend (/api/auth/complete)
 *
 * Falls back to simulated mode if WebAuthn is not available.
 */
export function useWebAuthn() {
  const isSupported = useMemo(() => {
    return (
      typeof window !== 'undefined' &&
      window.PublicKeyCredential !== undefined &&
      typeof window.PublicKeyCredential === 'function'
    )
  }, [])

  const authenticate = useCallback(async (): Promise<AuthResult> => {
    if (!isSupported) {
      return { success: false, error: 'WebAuthn is not supported in this browser' }
    }

    try {
      // Step 1: Get authentication options from server
      const options = await authService.beginAuthentication()

      // Step 2: Start the WebAuthn assertion ceremony
      // This triggers the browser's native "Insert your security key" dialog
      const assertion = await startAuthentication(options)

      // Step 3: Send the assertion to the server for verification
      const verification = await authService.completeAuthentication(assertion)

      if (verification.verified) {
        return { success: true, user: verification.user }
      } else {
        return { success: false, error: 'Credential verification failed' }
      }
    } catch (err) {
      // Handle common WebAuthn errors with user-friendly messages
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          return {
            success: false,
            error: 'Authentication was cancelled or timed out. Please try again.',
          }
        }
        if (err.name === 'SecurityError') {
          return {
            success: false,
            error: 'Security error. Ensure you are using HTTPS.',
          }
        }
        if (err.name === 'AbortError') {
          return {
            success: false,
            error: 'Authentication was cancelled.',
          }
        }
        return { success: false, error: err.message }
      }
      return { success: false, error: 'An unexpected error occurred' }
    }
  }, [isSupported])

  return { authenticate, isSupported }
}
